import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { SupplierRiskScore as SupplierRiskScoreType } from "../../../types/contractDashboard";
import styles from "./supplierRiskScore.module.scss";

interface SupplierRiskScoreProps {
  data: SupplierRiskScoreType[];
}

const SupplierRiskScore: React.FC<SupplierRiskScoreProps> = ({ data }) => {
  const suppliers = data.map(supplier => ({
    name: supplier.supplier_name,
    financial: Math.round(supplier.financial_health_risk_score),
    geopolitical: Math.round(supplier.geo_political_score),
    esg: Math.round(supplier.esg_score),
    cyber: Math.round(supplier.cyber_risk_score),
    overall: parseFloat(supplier.overall_score.toFixed(2)),
    risk: supplier.risk_level,
  }));

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#10b981";
    if (score >= 70) return "#f59e0b";
    return "#ef4444";
  };

  const getRiskIcon = (risk: string) => {
    if (risk === "Low")
      return (
        <CheckCircleIcon
          className={styles.riskIcon}
          style={{ color: "#10b981" }}
        />
      );
    if (risk === "Medium")
      return (
        <WarningIcon className={styles.riskIcon} style={{ color: "#f59e0b" }} />
      );
    return (
      <ErrorIcon className={styles.riskIcon} style={{ color: "#ef4444" }} />
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Supplier Risk Score
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Composite of financial health, geopolitical exposure, ESG, and cyber
          risk.
        </Typography>
      </Box>

      <TableContainer component={Paper} className={styles.tableContainer}>
        <Table className={styles.table}>
          <TableHead>
            <TableRow className={styles.headerRow}>
              <TableCell className={styles.headerCell}>SUPPLIER</TableCell>
              <TableCell className={styles.headerCell}>
                FINANCIAL HEALTH
              </TableCell>
              <TableCell className={styles.headerCell}>GEOPOLITICAL</TableCell>
              <TableCell className={styles.headerCell}>ESG SCORE</TableCell>
              <TableCell className={styles.headerCell}>CYBER RISK</TableCell>
              <TableCell className={styles.headerCell}>OVERALL SCORE</TableCell>
              <TableCell className={styles.headerCell}>RISK LEVEL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier, index) => (
              <TableRow key={index} className={styles.dataRow}>
                <TableCell className={styles.dataCell}>
                  {supplier.name}
                </TableCell>
                <TableCell className={styles.dataCell}>
                  <Box
                    className={styles.scoreCell}
                    style={{ color: getScoreColor(supplier.financial) }}
                  >
                    {supplier.financial}
                  </Box>
                </TableCell>
                <TableCell className={styles.dataCell}>
                  <Box
                    className={styles.scoreCell}
                    style={{ color: getScoreColor(supplier.geopolitical) }}
                  >
                    {supplier.geopolitical}
                  </Box>
                </TableCell>
                <TableCell className={styles.dataCell}>
                  <Box
                    className={styles.scoreCell}
                    style={{ color: getScoreColor(supplier.esg) }}
                  >
                    {supplier.esg}
                  </Box>
                </TableCell>
                <TableCell className={styles.dataCell}>
                  <Box
                    className={styles.scoreCell}
                    style={{ color: getScoreColor(supplier.cyber) }}
                  >
                    {supplier.cyber}
                  </Box>
                </TableCell>
                <TableCell className={styles.dataCell}>
                  <Box
                    className={styles.scoreCell}
                    style={{ color: getScoreColor(supplier.overall) }}
                  >
                    {supplier.overall}
                  </Box>
                </TableCell>
                <TableCell className={styles.dataCell}>
                  <Box className={styles.riskCell}>
                    {getRiskIcon(supplier.risk)}
                    <Typography variant="body2" className={styles.riskText}>
                      {supplier.risk}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SupplierRiskScore;
